import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useMutation, useQueryClient } from 'react-query';
import { CerebroTextButton } from '../../shared/components';
import { useApi } from '../../shared/context';
import { QueryKeys } from '../../shared/hooks';
import { DataRoomDocument, DataRoomFolder } from '../../shared/models';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { getResponseData } from '../../shared/utils/axios';

interface RemoveDocumentDialogProps {
	dataRoomGuid: string | undefined;
	folder: DataRoomFolder | null | undefined;
	document: DataRoomDocument | null;
	close: () => void;
}

export const RemoveDocumentDialog: React.FC<RemoveDocumentDialogProps> = ({
	dataRoomGuid,
	folder,
	document,
	close,
}) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();

	const onRemoveConfirmation = () => {
		if (document !== null) removeDocument(document);
	};

	const { mutate: removeDocument } = useMutation<DataRoomDocument, unknown, DataRoomDocument>(
		documentToDelete =>
			getResponseData(
				api.delete<DataRoomDocument>(
					CerebroApiEndpoints.Documents.Specific(dataRoomGuid, documentToDelete.guid)
				)
			),
		{
			onMutate: () => {
				close();
			},
			onSuccess: (_data, deletedDocument) => {
				client.invalidateQueries(QueryKeys.Documents.Root(dataRoomGuid, folder?.guid));
				snackbar.enqueueSnackbar('Document Deleted.', { variant: 'success' });

				close();
			},
			onError: () => {
				client.invalidateQueries(QueryKeys.Teasers.Root(dataRoomGuid));
				snackbar.enqueueSnackbar('There was a problem deleting the document.', { variant: 'error' });
			},
		}
	);

	return (
		<Dialog open={document !== null} onClose={close}>
			<DialogTitle>Confirm Delete</DialogTitle>
			<DialogContent>
				<DialogContentText>Are you sure you want to delete this document: {document?.name}?</DialogContentText>
			</DialogContent>
			<DialogActions>
				<CerebroTextButton onClick={close}>No</CerebroTextButton>
				<CerebroTextButton onClick={onRemoveConfirmation}>Yes</CerebroTextButton>
			</DialogActions>
		</Dialog>
	);
};
